import { useState, useEffect, useRef } from "react";
import { ChevronDown, User, LogOut, Menu, X, Globe, Plane } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { name: "Accueil", path: "/" },
  { name: "Procédures de voyage", path: "/procedure" },
  { name: "À propos", path: "/a-propos" },
  { name: "Evenement", path: "/evenement" },
];

const supportLinks = [
  { name: "FAQ", path: "/faq" },
  { name: "Tutoriels", path: "/tutoriels" },
  { name: "Contact", path: "/contact" },
];

const languages = [
  { code: "FR", label: "Français" },
  { code: "EN", label: "English" },
  { code: "AR", label: "العربية" },
];

export default function Navbar({ authenticated, user, logout, transparent = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showSupportMenu, setShowSupportMenu] = useState(false);
  const [currentLang, setCurrentLang] = useState("FR");
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);
  const supportMenuRef = useRef(null);

  const isActive = (link) => {
    if (link.path === "/" && location.pathname === "/") return true;
    if (link.path !== "/" && location.pathname.startsWith(link.path)) return true;
    return false;
  };

  const isSupportActive = supportLinks.some((l) => location.pathname.startsWith(l.path));

  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setShowLangMenu(false);
      if (supportMenuRef.current && !supportMenuRef.current.contains(e.target)) setShowSupportMenu(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHero = transparent && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors ${
        onHero ? "bg-transparent" : "bg-white shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <button onClick={() => navigate("/")} className="flex items-center flex-shrink-0">
            <img
              src={onHero ? "/logo_t.png" : "/logo.png"}
              alt="Niger Voyage"
              className="h-20 md:h-24 w-auto object-contain transition-all"
            />
          </button>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => {
              const active = isActive(link);
              const base = "text-sm font-medium whitespace-nowrap transition-colors pb-0.5";
              const color = onHero
                ? active
                  ? "text-white border-b-2 border-white"
                  : "text-white/80 hover:text-white"
                : active
                ? "text-brand-600 border-b-2 border-brand-600"
                : "text-slate-600 hover:text-brand-600";
              return (
                <button key={link.name} onClick={() => navigate(link.path)} className={`${base} ${color}`}>
                  {link.name}
                </button>
              );
            })}

            {/* Support dropdown */}
            <div className="relative" ref={supportMenuRef}>
              <button
                onClick={() => setShowSupportMenu(!showSupportMenu)}
                className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors pb-0.5 ${
                  onHero
                    ? isSupportActive
                      ? "text-white border-b-2 border-white"
                      : "text-white/80 hover:text-white"
                    : isSupportActive
                    ? "text-brand-600 border-b-2 border-brand-600"
                    : "text-slate-600 hover:text-brand-600"
                }`}
              >
                Support <ChevronDown size={13} />
              </button>
              {showSupportMenu && (
                <div className="absolute left-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50">
                  {supportLinks.map((link) => (
                    <button
                      key={link.name}
                      onClick={() => {
                        navigate(link.path);
                        setShowSupportMenu(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-brand-50 hover:text-brand-600"
                    >
                      {link.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            {/* Language */}
            <div className="relative hidden md:block" ref={langMenuRef}>
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className={`flex items-center gap-1 text-sm px-2 py-1.5 rounded-lg transition-colors ${
                  onHero ? "text-white hover:bg-white/10" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Globe size={15} />
                <span className="font-medium">{currentLang}</span>
                <ChevronDown size={13} />
              </button>
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-36 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setCurrentLang(lang.code);
                        setShowLangMenu(false);
                      }}
                      className={`flex items-center justify-between w-full px-3 py-2 text-sm transition-colors ${
                        currentLang === lang.code
                          ? "text-brand-600 bg-brand-50"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {lang.label} {currentLang === lang.code && <span>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Auth */}
            {authenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    onHero
                      ? "border border-white/40 text-white hover:bg-white/10"
                      : "border border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <User size={15} />
                  <span>{user?.first_name || "Mon compte"}</span>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50">
                    <button
                      onClick={() => {
                        navigate("/dashboard");
                        setShowUserMenu(false);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-600 hover:bg-brand-50"
                    >
                      <User size={13} /> Mon espace
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                        navigate("/");
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:bg-red-50"
                    >
                      <LogOut size={13} /> Se déconnecter
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  onHero
                    ? "border border-white/40 text-white hover:bg-white/10"
                    : "border border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >
                <User size={15} /> Se connecter
              </button>
            )}

            {/* Mobile toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`lg:hidden p-1.5 rounded-lg transition-colors ${
                onHero ? "text-white hover:bg-white/10" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-4 py-3 space-y-1">
          {navLinks.map((link) => (
            <button
              key={link.name}
              onClick={() => {
                navigate(link.path);
                setIsMenuOpen(false);
              }}
              className={`block w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive(link)
                  ? "text-brand-600 bg-brand-50"
                  : "text-slate-700 hover:bg-brand-50 hover:text-brand-600"
              }`}
            >
              {link.name}
            </button>
          ))}
          <div className="pt-1 mt-1 border-t border-slate-100">
            <div className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">Support</div>
            {supportLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => {
                  navigate(link.path);
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm font-medium rounded-lg text-slate-700 hover:bg-brand-50 hover:text-brand-600"
              >
                {link.name}
              </button>
            ))}
          </div>
          <div className="pt-2 border-t border-slate-100 flex gap-2">
            {authenticated ? (
              <button
                onClick={() => {
                  logout();
                  setIsMenuOpen(false);
                  navigate("/");
                }}
                className="flex-1 py-2 rounded-lg bg-red-50 text-red-500 text-sm font-medium"
              >
                Se déconnecter
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    navigate("/login");
                    setIsMenuOpen(false);
                  }}
                  className="flex-1 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium"
                >
                  Connexion
                </button>
                <button
                  onClick={() => {
                    navigate("/inscription");
                    setIsMenuOpen(false);
                  }}
                  className="flex-1 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold"
                >
                  S'inscrire
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
